import { Link, useNavigate, useParams } from 'react-router-dom'
import { useManifest } from '@/core/hooks'
import { useConnections } from '@/core/connection/connection.context'
import { ConnectionCard } from '@/core/connection/ConnectionCard'
import {
  Callout,
  ErrorNotice,
  Loading,
  Logo,
  MethodTag,
} from '@/shared/components'

export function ConnectorDetailPage() {
  const { connectorId = '' } = useParams()
  const navigate = useNavigate()
  const manifest = useManifest(connectorId)
  const { byConnector } = useConnections()

  const connector = manifest.data
  const existing = byConnector[connectorId] ?? []
  const needsSetup = Boolean(connector) && !connector!.oauth_ready

  return (
    <div className="page">
      <Link to="/catalog" className="small">
        ← Catalog
      </Link>

      {manifest.isLoading && <Loading />}
      {manifest.error && <ErrorNotice error={manifest.error} onRetry={() => manifest.refetch()} />}

      {connector && (
        <>
          <div className="page-header" style={{ marginTop: 14 }}>
            <div className="page-header-row">
              <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                <Logo src={connector.logo} name={connector.name} id={connector.id} />
                <div>
                  <h1>{connector.name}</h1>
                  <p>{connector.description}</p>
                  <div className="row small faint" style={{ marginTop: 8 }}>
                    <span className="badge badge-neutral">{connector.category}</span>
                    <span className="badge badge-neutral">{connector.auth_methods.length} auth option{connector.auth_methods.length === 1 ? '' : 's'}</span>
                    <span className="mono">{connector.base_url}</span>
                    {connector.docs_url && (
                      <a href={connector.docs_url} target="_blank" rel="noreferrer noopener">
                        API docs ↗
                      </a>
                    )}
                  </div>
                </div>
              </div>
              {!needsSetup && (
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/connections/new/${connector.id}`)}
                >
                  {existing.length > 0 ? 'Connect another account' : 'Connect'}
                </button>
              )}
            </div>
          </div>

          {needsSetup && (
            <Callout kind="warn" title="Server setup required">
              <p className="small">
                None of {connector.name}'s authorization types can be used yet.
                {' '}
                {connector.auth_methods
                  .flatMap((m) => m.required_env)
                  .join(', ') || 'Check the server configuration'}
                {' must be set in the backend environment, then restart the API.'}
              </p>
            </Callout>
          )}

          <div className="card stack" style={{ marginTop: 16, gap: 11 }}>
            <h3>Ways to connect</h3>
            {connector.auth_methods.map((m) => (
              <div key={m.id} className="stack" style={{ gap: 5 }}>
                <div className="row-tight">
                  <strong>{m.label}</strong>
                  <span className="badge badge-neutral mono">{m.type}</span>
                  {m.transport !== 'http' && (
                    <span className="badge badge-warn">{m.transport}</span>
                  )}
                  {!m.ready && <span className="badge badge-warn">setup needed</span>}
                </div>
                {m.description && (
                  <p className="small muted" style={{ margin: 0 }}>
                    {m.description}
                  </p>
                )}
                {!m.ready && m.required_env.length > 0 && (
                  <p className="small mono faint" style={{ margin: 0 }}>
                    needs {m.required_env.join(' and ')} on the server
                  </p>
                )}
                {m.scopes.length > 0 && (
                  <div className="row" style={{ gap: 4 }}>
                    {m.scopes.map((scope) => (
                      <span key={scope} className="badge badge-neutral mono">
                        {scope.replace(/^https:\/\/www\.googleapis\.com\/auth\//, '')}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {existing.length > 0 && (
            <>
              <h2 style={{ margin: '26px 0 12px' }}>
                Your {connector.name} connections
              </h2>
              <div className="grid">
                {existing.map((conn) => (
                  <ConnectionCard key={conn.connection_id} connection={conn} />
                ))}
              </div>
            </>
          )}

          <h2 style={{ margin: '26px 0 12px' }}>
            Actions ({connector.actions.length})
          </h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 62 }}>Method</th>
                  <th>Action</th>
                  <th>Description</th>
                  <th>Scopes</th>
                </tr>
              </thead>
              <tbody>
                {connector.actions.map((action) => (
                  <tr key={action.id}>
                    <td>
                      <MethodTag method={action.method} />
                    </td>
                    <td>
                      <div style={{ fontWeight: 550 }}>{action.name}</div>
                      <div className="mono faint">{action.id}</div>
                      {action.is_lookup && (
                        <span className="badge badge-accent" style={{ marginTop: 3 }}>
                          lookup
                        </span>
                      )}
                    </td>
                    <td className="small muted">{action.description}</td>
                    <td className="small mono faint">
                      {action.required_scopes.join(', ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </>
      )}
    </div>
  )
}
